import { useEffect, useMemo } from 'react';

import { useQuery } from '@apollo/client/react';

import { gql } from '@graphql/__generated__';
import type { UserSelectFragment } from '@graphql/__generated__/graphql';

const GET_TEAM_BROKER_OPTIONS = gql(/* GraphQL */ `
  query GetTeamBrokerOptions($team_id: uuid!) {
    teams_users(
      where: {
        team_id: { _eq: $team_id }
        deactivated: { _eq: false }
        user: { is_broker: { _eq: true } }
      }
      order_by: { user: { full_name: asc } }
    ) {
      user {
        id
        ...UserSelect
      }
    }
  }
`);

// Stable reference: `UserSelect` resets its option list whenever
// `defaultOptions` changes identity.
const EMPTY_OPTIONS: UserSelectFragment[] = [];

/** Active brokers of a team, shaped for `UserSelect.defaultOptions`. */
export const useTeamBrokerOptions = (
  teamId: string | null | undefined,
): UserSelectFragment[] => {
  const { data, error } = useQuery(GET_TEAM_BROKER_OPTIONS, {
    variables: { team_id: teamId ?? '' },
    skip: teamId == null || teamId === '',
  });

  // The picker degrades to search-only on failure; keep the cause visible.
  useEffect(() => {
    if (error != null) {
      console.error('[useTeamBrokerOptions]', error);
    }
  }, [error]);

  return useMemo(
    () =>
      data == null ? EMPTY_OPTIONS : data.teams_users.map(entry => entry.user),
    [data],
  );
};
